// @ts-strict-ignore
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { DirectoryContact } from 'rainbow-web-sdk/models/directoryContact.model';
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { DirectoryInternalSearchResults, DirectorySearchOptions } from '../core/directorySearch.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { ErrorHelperInfo, ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { EventService } from 'rainbow-web-sdk/services/core/event.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { CountryService } from 'rainbow-web-sdk/services/core/country.service';
import { UserInfoService } from 'rainbow-web-sdk/services/core/userInfo.service';
import { FavoritesServiceRB } from 'rainbow-web-sdk/services/favorites/favorites.service';
import { Service } from 'rainbow-web-sdk/services/service';
import xmlParser, { XMLElement, XMLElements } from 'rainbow-web-sdk/utils/xmlParser';
import anonymizer from '../../utils/anonymizer';

export const PERSONAL_DIRECTORY_SVC = 'PersonalDirectoryService';

/**
 * @enum {string}
 * @public
 * @readonly
 * @description
 *      Collection of events fired by {@link PersonalDirectoryService} regarding the Personal Directory
 */
export enum PersonalDirectoryEvent {

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_ADD
     * @description
     *      This event is fired when a new contact is added in the personal directory
     *      The event contains a Contact object
     * @param {Contact} data containing the information of the personal contact
     */
    PERSONAL_DIRECTORY_ADD = "PERSONAL_DIRECTORY_ADD",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_DELETE
     * @description
     *      This event is fired when a contact is deleted from the personal directory
     *      The event contains the id of the deleted contact
     * @param {string} id containing the id of the deleted contact
     */
    PERSONAL_DIRECTORY_DELETE = "PERSONAL_DIRECTORY_DELETE",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_UPDATE
     * @description
     *      This event is fired when a contact is updated in the personal directory
     *      The event contains a Contact object
     * @param {Contact} data containing the information of the personal contact
     */
    PERSONAL_DIRECTORY_UPDATE = "PERSONAL_DIRECTORY_UPDATE",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_IMPORT_PROGRESS
     * @description
     *      This event is fired when import of personal directory is progressing
     *      The event contains a number
     * @param {number} percent containing the percent of the import
     */
    PERSONAL_DIRECTORY_IMPORT_PROGRESS = "PERSONAL_DIRECTORY_IMPORT_PROGRESS",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_IMPORT_COMPLETION
     * @description
     *      This event is fired when import of personal directory is completed
     */
    PERSONAL_DIRECTORY_IMPORT_COMPLETION = "PERSONAL_DIRECTORY_IMPORT_COMPLETION",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_DELETE_ALL
     * @description
     *      This event is fired when all contacts are removed from the personal directory of the user
     */
    PERSONAL_DIRECTORY_DELETE_ALL = "PERSONAL_DIRECTORY_DELETE_ALL",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_TAG_DELETE
     * @description
     *      This event is fired when a tag is removed from all the personal directory entries
     *      The event contains the name of the removed tag
     * @param {string} name of the removed tag
     */
    PERSONAL_DIRECTORY_TAG_DELETE = "PERSONAL_DIRECTORY_TAG_DELETE",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_TAG_RENAME
     * @description
     *      This event is fired when a tag is renamed inside all personal directory entries
     *      The event contains the old anf the new name of the tag
     * @param {string} Old name of the removed tag
     * @param {string} New name of the removed tag
     */
    PERSONAL_DIRECTORY_TAG_RENAME = "PERSONAL_DIRECTORY_TAG_RENAME",

    /**
     * @public
     * @event PersonalDirectoryEvent#PERSONAL_DIRECTORY_UPDATE_ALL
     * @description
     *      This event is fired when all personal directory contacts stored in internal cache have been updated.
     *      This happens for example after a XMPP reconnection, these contacts must be updated again
     *      (thanks to a new 'getPersonalContacts') as during the time of disconnection such contacts may have been
     *      added, updated or deleted by the end-user through another Rainbow client.
     */
    PERSONAL_DIRECTORY_UPDATE_ALL = "PERSONAL_DIRECTORY_UPDATE_ALL",

}

export class PersonalDirectoryService extends Service {

    // ====================================================
    // Services used
    // ====================================================
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private countryService: CountryService = CountryService.getInstance();
    private userInfoService: UserInfoService = UserInfoService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private favoritesService: FavoritesServiceRB = FavoritesServiceRB.getInstance();

    private rxSubject: Subject<any> = new Subject<any>();
    public xmppManagementHandler: any = null;

    // Observable indicating when the start of the PersonalDirectory Service is completed
    // This includes especially when all personal contacts are retrieved from server
    public serviceReady = new BehaviorSubject<boolean>(false);

    private directoryPortalURL: string;
    private massProPortalURL: string;
    private personalDirectoryEnabled: boolean = false;
    private deleteAllInProgress: boolean = false;

    private DEFAULT_CSV_BOM = "\uFEFF"; // a file encoded with UTF-8-sig starts with a BOM (byte order mark), which is useful for UTF-16 and UTF-32 but really unnecessary for UTF-8
    private DEFAULT_CSV_TYPE = "text/csv; charset=utf-8-sig";

    public static getInstance() {
        return Service.create(PERSONAL_DIRECTORY_SVC, PersonalDirectoryService);
    }

    public static build() {
        return new PersonalDirectoryService();
    }

    private constructor() { super(); }

    public async start() {
        try {
            this.logger.info(`[PersonalDirectoryService] === STARTING ===`);

            this.init();

            // Check licence
            if (this.personalDirectoryEnabled) {
                this.attachXMPPListeners();
                const allPersoContacts = await this.getPersonalContacts();
                this.logger.debug(`[PersonalDirectoryService] Nb Contact found = ${allPersoContacts.total}`);
            } else {
                this.logger.warn(`[PersonalDirectoryService] User does not have the profile feature PERSONAL_DIRECTORY`);
            }

            // PersonalDirectory Service is ready to be used by other services/components
            this.serviceReady.next(true);

            this.logger.info(`[PersonalDirectoryService] === STARTED ===`);
        }
        catch (error) {
            this.logger.error(`[PersonalDirectoryService] === STARTING FAILURE === ${error.message}`);
        }
    }

    public async stop() {
        this.logger.info(`[PersonalDirectoryService] === STOPPING ===`);
        this.removeXMPPListeners();
        this.logger.info(`[PersonalDirectoryService] === STOPPED ===`);
    }

    public async reconnect(): Promise<void> {
        this.logger.info(`[PersonalDirectoryService] Reconnect in progress...`);
        await this.stop();
        await this.start();
        this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE_ALL));
        this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE_ALL);
        this.logger.info(`[PersonalDirectoryService] Reconnect -- success`);
    }

    public init(): void {
        // Get and store Directory portalURL
        this.directoryPortalURL = config.restServerUrl + "/api/rainbow/directory/v1.0";
        this.massProPortalURL = config.restServerUrl + "/api/rainbow/massprovisioning/v1.0/";

        // Get Personal Directory
        // Personal Directory is only available on Business and Enterprise licenses (including the Voice licenses).
        // Feature (adding contacts, searching contacts, ...) is controlled by the flag PERSONAL_DIRECTORY.
        this.personalDirectoryEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.PERSONAL_DIRECTORY);
        this.logger.info(`[PersonalDirectoryService] Personal Directory status from profileService: ${this.personalDirectoryEnabled}`);
    }

    // ====================================================
    //  EVENT LISTENERS ( Handling of calendar events)
    // ====================================================

    private attachXMPPListeners() {
        this.logger.info(`[PersonalDirectoryService] attachXMPPListeners`);
        this.removeXMPPListeners();
        this.xmppManagementHandler = this.xmppService.connection.addHandler((stanza: any) => { this.onDirectoryManagementReceived(stanza); return true; }, null, "message", "management");
    }

    private removeXMPPListeners() {
        if (this.xmppManagementHandler) { this.xmppService.connection.deleteHandler(this.xmppManagementHandler); this.xmppManagementHandler = null; }

    }

    public subscribe(handler: any): Subscription {
        return this.rxSubject.subscribe(handler);
    }

    // ------------------------------------------------------------------------
    //  PERSONAL CONTACTS MANAGEMENT from received XMPP Message
    // ("message" type="management")
    // ------------------------------------------------------------------------

    private onDirectoryManagementReceived(stanza: any): void {
        try {
            const stanzaElem: XMLElement = xmlParser(stanza);
            const directoryElem: XMLElements = stanzaElem.find("directory");
            const importStatusElem: XMLElements = stanzaElem.find("import_status");

            if (directoryElem.length > 0) {
                // Get personal directory identifier
                const persoDirId = directoryElem.attr("id");

                if (!persoDirId) {
                    throw Error("[PersonalDirectoryService] onDirectoryManagementReceived -- No directory id received for event ${action}");
                }

                // Handle personal directory action events
                const action = directoryElem.attr("action");
                this.logger.debug(`[PersonalDirectoryService] onDirectoryManagementReceived -- ${action} event received on personal directory id ${persoDirId}`);
                switch (action) {
                    case 'create':
                        this.onPersonalDirectoryCreation(directoryElem);
                        break;
                    case 'update':
                        this.onPersonalDirectoryUpdate(directoryElem);
                        break;
                    case 'delete':
                        this.onPersonalDirectyDeletion(persoDirId);
                        break;
                    default: break;
                }
            }
            if (importStatusElem.length > 0) {
                // Get personal directory identifier
                const seqNumber = importStatusElem.attr("seq");
                const status = importStatusElem.attr("status");

                if (!seqNumber) {
                    throw Error("[PersonalDirectoryService] onDirectoryManagementReceived -- No Import - Seq Number received");
                }
                if (!status) {
                    throw Error("[PersonalDirectoryService] onDirectoryManagementReceived -- No Import - Status received");
                }

                // Handle personal directory action events
                this.logger.debug(`[PersonalDirectoryService] onDirectoryManagementReceived -- Status : ${status}`);
                if (status === 'Initializing') {
                    this.onPersonalDirectoryImportInitializing();
                } else if (status === 'Deleting') {
                    this.onPersonalDirectoryImportDeletion();
                } else if (status === 'Creating') {
                    this.onPersonalDirectoryImportCreation(seqNumber);
                } else if (status === 'Completed successfully' || status === 'Completed with failure') {
                    this.onPersonalDirectoryImportSuccess();
                }
            }
        }
        catch (err) {
            this.logger.error("[PersonalDirectoryService] onDirectoryManagementReceived -- failure -- " + err.message);
        }
    }

    private onPersonalDirectoryImportInitializing(): void {
        this.logger.debug("[PersonalDirectoryService] >onPersonalDirectoryImportInitializing");

        this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_PROGRESS, 0));
        this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_PROGRESS);
    }

    private onPersonalDirectoryImportDeletion(): void {
        this.logger.debug("[PersonalDirectoryService] >onPersonalDirectoryImportDeletion");

        try {
            const personalDirContacts: Contact[] = this.contactService.getPersonnalDirectoryContacts();
            if (personalDirContacts && personalDirContacts.length) {
                personalDirContacts.forEach((persoContact) => {
                    this.contactService.removeDirectoryContactFromCache(persoContact);
                });
                this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE_ALL));
                this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE_ALL);
            }
        }
        catch (err) {
            this.logger.error("[PersonalDirectoryService] onDirectoryManagementReceived -- failure -- " + err.message);
        }
    }

    private onPersonalDirectoryImportCreation(seqNumber: string): void {
        this.logger.debug("[PersonalDirectoryService] >onPersonalDirectoryImportCreation : " + seqNumber);

        switch (seqNumber) {
            case '3':
                this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_PROGRESS, 33));
                break;
            case '4':
                this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_PROGRESS, 66));
                break;
            case '5':
                this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_PROGRESS, 100));
                break;
            default:
                break;
        }
    }

    private async onPersonalDirectoryImportSuccess(): Promise<void> {
        this.logger.debug("[PersonalDirectoryService] >onPersonalDirectoryImportSuccess");

        //update the list of personal contacts after update
        await this.getPersonalContacts();

        this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_COMPLETION));
        this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_IMPORT_COMPLETION);
    }

    private onPersonalDirectyDeletion(persoDirId: string): void {
        // Get the associated contact instance (normally it must exist)
        const deletedContact: Contact = this.contactService.getContactFromCacheByJid(persoDirId);

        // If found in internal Contact DB and not already been marked as obsolete,
        // mark the deleted contact as obsolete so that it should not be used by internal service such as the Call Log.
        if (deletedContact) {
            this.logger.info(`[PersonalDirectoryService] onPersonalDirectyDeletion -- Set directory id ${persoDirId} as obsolete`);

            this.contactService.removeDirectoryContactFromCache(deletedContact);

            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, persoDirId));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, persoDirId);
        }
    }

    private buildContactCardFromXmpp(directoryElem: XMLElements): any {
        const contactCard: any = {
            id: directoryElem.attr("id"),
            userId: directoryElem.find("userId").text(),
            companyId: undefined,
            directoryType: "personal",
            type: directoryElem.find("type").text(),
            firstName: directoryElem.find("firstName").text(),
            lastName: directoryElem.find("lastName").text(),
            eMail: directoryElem.find("eMail").text(),
            jobTitle: directoryElem.find("jobTitle").text(),
            department: directoryElem.find("department").text(),
            companyName: directoryElem.find("companyName").text(),
            street: directoryElem.find("street").text(),
            city: directoryElem.find("city").text(),
            state: directoryElem.find("state").text(),
            postalCode: directoryElem.find("postalCode").text(),
            country: directoryElem.find("country").text(),
            custom1: directoryElem.find("custom1").text(),
            custom2: directoryElem.find("custom2").text(),
            workPhoneNumbers: [],
            mobilePhoneNumbers: [],
            otherPhoneNumbers: [],
            tags: []
        };

        // Manage element received as array (Phone numbers and tags)
        const tagsElem = directoryElem.find('tags');
        tagsElem.find('tag').forEach((tag) => {
            contactCard.tags.push(tag.text());
        });

        const workPhoneNumbersElem = directoryElem.find('workPhoneNumbers');
        workPhoneNumbersElem.find('phoneNumber').forEach((phoneNumber) => {
            contactCard.workPhoneNumbers.push(phoneNumber.text());
        });

        const mobilePhoneNumbersElem = directoryElem.find('mobilePhoneNumbers');
        mobilePhoneNumbersElem.find('phoneNumber').forEach((phoneNumber) => {
            contactCard.mobilePhoneNumbers.push(phoneNumber.text());
        });

        const otherPhoneNumbersElem = directoryElem.find('otherPhoneNumbers');
        otherPhoneNumbersElem.find('phoneNumber').forEach((phoneNumber) => {
            contactCard.otherPhoneNumbers.push(phoneNumber.text());
        });

        return contactCard;
    }

    private onPersonalDirectoryCreation(directoryElem: XMLElements): void {
        const contactCard: any = this.buildContactCardFromXmpp(directoryElem);

        // Before creating a a new Contact object, we will check if it has not already been created at reception
        // of the associted xmpp event. If the case, we don't create a new one but return the existing one.
        const contact: Contact = this.contactService.getContactFromCacheByJid(contactCard.id);

        if (contact) {
            this.logger.info(`[PersonalDirectoryService] onPersonalDirectoryCreation -- Contact id ${contactCard.id} exists in cache, no need to create it`);
            return;
        }

        this.logger.info(`[PersonalDirectoryService] onPersonalDirectoryCreation -- Create a new contact id ${contactCard.id} for user ${contactCard.userId}`);
        const directoryContact: DirectoryContact = DirectoryContact.createFromData(contactCard);

        // Create a new Contact object from the DirectoryContact model
        const createdContact: Contact = this.createContactFromPersonalDirectoryData(directoryContact);

        if (createdContact) {
            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_ADD, createdContact));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_ADD, createdContact);
            // Useful to eventually update the user infos on right panel
            createdContact.rxSubject.next({ name: 'update' });
        } else {
            this.logger.error(`[PersonalDirectoryService] onPersonalDirectoryCreation -- Failure when creating internal Contact (null)`);
        }

    }

    private onPersonalDirectoryUpdate(directoryElem: XMLElements): void {
        const contactCard: any = this.buildContactCardFromXmpp(directoryElem);

        const directoryContact: DirectoryContact = DirectoryContact.createFromData(contactCard);
        const updatedContact = this.updateContactFromPersonalDirectoryData(directoryContact);

        if (updatedContact) {
            this.logger.info(`[PersonalDirectoryService] onPersonalDirectoryUpdate -- Update Personal Contact ${contactCard.id} for user ${contactCard.userId}`);

            // Send notification about the update of the personal contact
            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, updatedContact));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, updatedContact);

            // Useful to update the data (display name, avatar) in the favorite list
            this.favoritesService.searchAndUpdateFavoriteContact(updatedContact);
            // Useful to eventually update the user infos on right panel
            updatedContact.rxSubject.next({ name: 'update' });
        } else {
            this.logger.error(`[PersonalDirectoryService] onPersonalDirectoryUpdate -- Failure when updating internal contact ${contactCard.id} for user ${contactCard.userId} (not found)`);
        }
    }

    // ====================================================
    //                 PUBLIC FUNCTIONS
    // ====================================================

    // ======================================================================
    // ===		PERSONAL CONTACT MANAGEMENT (Create/Update/Delete)
    // ======================================================================

    // Management of personal contact Creation/Update/Deletion
    // -------------------------------------------------------

    /**
     * Method createPersonalContact
     * @description This API allows users to create a contact in its personal directory
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-PostDirectory Rainbow Company Directory portal - directory - Create a directory entry})
     * @memberof PersonalDirectoryService
     * @public
     * @param {DirectoryContact} persDirContact a {@link DirectoryContact} The personal contact to create on server
     * @returns {Promise<Contact>} The created personal directory contact as a Contact model
     * @memberof PersonalDirectoryService
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_ADD
     */
    public async createPersonalContact(persDirContact: DirectoryContact): Promise<Contact | null> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] createPersonalContact -- User does not have the profile feature PERSONAL_DIRECTORY");

            // To be sure to not create a company contact, reset unwanted 'companyId' field
            if (persDirContact.companyId) {
                persDirContact.companyId = undefined;
            }

            const url: string = `${this.directoryPortalURL}/entries?analyticsEntryType=personal`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'POST', headers, body: JSON.stringify(persDirContact) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const contact: DirectoryContact = DirectoryContact.createFromData(responseData.data);
            this.logger.info(`[PersonalDirectoryService] createPersonalContact success from server for user ${contact.userId} -- Id = ${contact.id}`);

            // Before creating a new instance in internal Contact DB, we will check if it has not already been created at reception
            // of the associated xmpp event. If the case, we don't create a new one but return the existing instance.
            const newContact: Contact = this.contactService.getContactFromCacheByJid(contact.id);

            if (newContact) {
                this.logger.info(`[PersonalDirectoryService] createPersonalContact -- Contact Id ${contact.id} already created (maybe on xmpp event)`);
                return newContact;
            }

            // Convert the created personal contact translated as a new Contact object
            const createdContact: Contact = this.createContactFromPersonalDirectoryData(contact);

            if (createdContact) {
                this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_ADD, createdContact));
                this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_ADD, createdContact);
                // Useful to eventually update the user infos on right panel
                createdContact.rxSubject.next({ name: 'update' });
            } else {
                throw Error("[PersonalDirectoryService] createPersonalContact -- Failure when creating internal Contact (null)");
            }

            // return created a Contact mode associated to the  personal directory contact
            return createdContact;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'createPersonalContact');
            this.logger.error(`[PersonalDirectoryService] createPersonalContact failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method deletePersonalContact
     * @description This API allows users to delete a personal directory contact
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-DeleteDirectory Rainbow Company Directory portal - directory - Delete a directory entry})
     * @memberof PersonalDirectoryService
     * @public
     * @param {Contact} contact Contact instance associated to the personal directory contact to delete
     * @returns {Promise<void>} resolves when deletion is done
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_DELETE
     */
    public async deletePersonalContact(contact: Contact): Promise<void> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] deletePersonalContact -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/${contact.id}`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info(`[PersonalDirectoryService] Delete of Personal Contact ${contact.id} success from server for user ${contact.userId}`);

            this.contactService.removeDirectoryContactFromCache(contact);

            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, contact.id));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, contact.id);

            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deletePersonalContact');
            this.logger.error(`[PersonalDirectoryService] deletePersonalContact failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method deletePersonalContactById
     * @description This API allows users to delete a personal directory contact
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-DeleteDirectory Rainbow Company Directory portal - directory - Delete a directory entry})
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} id Identifier of the personal directory contact to delete
     * @returns {Promise<void>} resolves when deletion is done
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_DELETE
     */
    public async deletePersonalContactById(id: string): Promise<void> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] deletePersonalContactById -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/${id}`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }

            this.logger.info(`[PersonalDirectoryService] Delete of Personal Contact of ${id} success from server`);

            // Get associated internal Contact DB
            const contact: Contact = this.contactService.getContactFromCacheByJid(id);

            // The current deleted contact must be marked as obsolete but only if this has not already been done, for example,
            // at reception of the associated xmpp event. But if the case, nothing to do at reception of the server response.
            if (contact) {
                this.contactService.removeDirectoryContactFromCache(contact);

                // Don't send an event if the unitary personal contact deletion comes from a 'Delete All' request
                // Indeed, this is an optimization to avoid to send several 'PERSONAL_DIRECTORY_DELETE' events  while
                // a 'PERSONAL_DIRECTORY_DELETE_ALL' event will be send as global notif
                if (!this.deleteAllInProgress) {
                    this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, contact.id));
                    this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE, contact.id);
                }
            }
            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deletePersonalContactById');
            this.logger.error(`[PersonalDirectoryService] deletePersonalContactById failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method deleteAllContacts
     * @description This API allows users to delete all the contacts present in its personal directory
     * @memberof PersonalDirectoryService
     * @public
     * @returns {Promise<void>} resolves when deletion is done
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_DELETE_ALL
     */
    public async deleteAllPersonalContacts(): Promise<void> {
        try {
            const promiseArray: Promise<any>[] = [];

            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] deleteAllPersonalContacts -- User does not have the profile feature PERSONAL_DIRECTORY");

            const getResults: any = await this.getPersonalContacts();

            this.deleteAllInProgress = true;
            getResults.contacts.forEach((persContact) => {
                promiseArray.push(this.deletePersonalContactById(persContact.id));
            });
            await Promise.all(promiseArray);
            this.deleteAllInProgress = false;

            this.logger.info(`[PersonalDirectoryService] Delete of all Personal Contacts success from server`);
            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE_ALL));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_DELETE_ALL);

            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deleteAllPersonalContacts');
            this.logger.error(`[PersonalDirectoryService] deleteAllPersonalContacts failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method updatePersonalContactFromUserData
     * @description This API allows users to update a personal contact present in its personal directory
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-PutDirectory Rainbow Company Directory portal - directory - Update a directory entry})
     * @memberof PersonalDirectoryService
     * @public
     * @param {Contact} contact Contact instance associated to the personal directory contact to update
     * @returns {Promise<Contact>} The created personal directory contact as a Contact model
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_UPDATE
     */
    public async updatePersonalContactFromUserData(contact: Contact): Promise<Contact | null> {
        try {
            const url: string = `${this.directoryPortalURL}/entries/${contact.id}`;
            const headers = this.authService.getPostHeader();

            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] updatePersonalContactFromUserData -- User does not have the profile feature PERSONAL_DIRECTORY");

            // Convert the Contact into a personal directory contact as known from server
            const persDirectoryContact: any = this.createPersonalDirectoryDataFromContact(contact);

            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(persDirectoryContact) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());

            // OPEN Point: Once operation is done on server, is it mandatory to update once again the contact for security ?
            //             Indeed, the contact passed in parameter is supposed to already contain the last modifications
            // Convert the personal directory contact (server format) into a directory contact object
            const dirContact: DirectoryContact = DirectoryContact.createFromData(responseData.data);

            const contactUpdated = this.updateContactFromPersonalDirectoryData(dirContact);

            this.logger.info(`[PersonalDirectoryService] Update Personal Contact ${contactUpdated.id} success from server for user ${contactUpdated.userId}`);

            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, contactUpdated));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, contactUpdated);

            // Useful to update the data (display name, avatar) in the favorite list
            this.favoritesService.searchAndUpdateFavoriteContact(contactUpdated);
            // Useful to eventually update the user infos on right panel
            contactUpdated.rxSubject.next({ name: 'update' });

            return contactUpdated;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updatePersonalContactFromUserData');
            this.logger.error(`[PersonalDirectoryService] updatePersonalContactFromUserData failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method updatePersonalContact
     * @description This API allows users to update an contact present in its personal directory.
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-PutDirectory Rainbow Company Directory portal - directory - Update a directory entry})
     * @memberof PersonalDirectoryService
     * @public
     * @param {Contact} persDirContact Contact instance associated to the personal directory contact to update
     * @returns {Promise<Contact>} The created personal directory contact as a Contact model
     * @fires PersonalDirectoryEvent#PERSONAL_DIRECTORY_UPDATE
     */
    public async updatePersonalContact(persDirContact: DirectoryContact): Promise<Contact | null> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] updatePersonalContact -- User does not have the profile feature PERSONAL_DIRECTORY");

            // To be sure that the personal contact card contains well the Identifier
            if (!persDirContact.id) {
                throw (new Error('Can not update  Personal Contact, missing Id'));
            }

            const url: string = `${this.directoryPortalURL}/entries/${persDirContact.id}`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(persDirContact) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            // Convert the personal directory contact (server format) into a directory contact object
            const dirContact: DirectoryContact = DirectoryContact.createFromData(responseData.data);

            const contactUpdated = this.updateContactFromPersonalDirectoryData(dirContact);

            this.logger.info(`[PersonalDirectoryService] Update Personal Contact ${contactUpdated.id} success from server for user ${contactUpdated.userId}`);

            // Send notification about the update of the personal contact
            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, contactUpdated));
            this.eventService.publish(PersonalDirectoryEvent.PERSONAL_DIRECTORY_UPDATE, contactUpdated);

            // Useful to update the data (display name, avatar) in the favorite list
            this.favoritesService.searchAndUpdateFavoriteContact(contactUpdated);
            // Useful to eventually update the user infos on right panel
            contactUpdated.rxSubject.next({ name: 'update' });

            return contactUpdated;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updatePersonalContact');
            this.logger.error(`[PersonalDirectoryService] updatePersonalContact failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // Management of personal contacts Get & Search
    // --------------------------------------------

    /**
     * Method getPersonalContacts
     * @description This API allows users to retrieve the list of the personal directory contacts of the user with full information pour each of them
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} [format] Allows to retrieve more or less contact details in response.
     *      - small: id, directoryType, firstName, lastName
     *      - medium: id, directoryType, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @returns {Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number }>} object contaning result of search with
     * {
     *   contacts :  {array}     // Found personal directory contacts
     *   limit :     {number}    // Limit specified at the search request, fix in our case by default to max possible.
     *   offset :    {number}    // The position of first retrieved contact entry
     *   total :     {number}    // Total number of available personal directory contacts (May be different from the number of returned contacts)
     * }
     */
    public async getPersonalContacts(format: string = 'full'): Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number; }> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalContacts -- User does not have the profile feature PERSONAL_DIRECTORY");

            // Prepare request url
            const directoryScope: string = "personal";
            const entryType: string = "all";
            const limitForAll: number = 1000; // To be sure to get all contacts in one short from server (Maybe not so good for the perf - Server limit)
            const url: string = `${this.directoryPortalURL}/entries?view=${directoryScope}&type=${entryType}&limit=${limitForAll}&format=${format}`;

            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info(`[PersonalDirectoryService] getPersonalContacts success -- Found ${responseData.total} contact(s)`);
            const contactsData = responseData.data;
            const persDirContacts: DirectoryContact[] = [];

            contactsData.forEach((contactData) => {
                // Convert the personal directory contact (server format) into a directory contact object
                const persDirContact: DirectoryContact = DirectoryContact.createFromData(contactData);

                // Create a new Contact object or update the existing one with the retrieved personal contact
                this.createOrUpdateContact(persDirContact);

                persDirContacts.push(persDirContact);
            });

            return { contacts: persDirContacts, limit: responseData.limit, offset: responseData.offset, total: responseData.total };

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalContacts');
            this.logger.error(`[PersonalDirectoryService] getPersonalContacts failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method getPersonalContactsByCriteria
     * @description This API allows users to get a list of personal directory contacts data by optionally specifying
     * - Data format
     * - Pagination criteria (offset, limit)
     * - Search criteria (mode, pattern)
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @memberof PersonalDirectoryService
     * @public
     * @param {object} [options] Search filter and criteria specified via the Option object
     * @property {string} options Option object that may contain the following properties:
     * @property {string} [options.format='full'] Allows to retrieve more or less contact details in response.
     *      - small: id, directoryType, firstName, lastName
     *      - medium: id, directoryType, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @property {number} [options.limit] Allows to specify the number personal directory contacts to retrieve.
     * Default value: 100
     * @property {number} [options.offset] Allows to specify the position of first personal directory contact to retrieve
     * If not specified, the search begins at the beginning, from the the first personal directory contact.
     * Warning: if offset > total, no results are returned.
     * @property {string} [options.searchBy] Allows to filter the list of directory entries on phone number or on the name provided in this option.
     * @property {string} [options.searchPattern] Allows to filter the list of directory entries by the words provided in this option
     * @returns {Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number }>} object contaning result of search with
     * {
     *   contacts :  {array}     // Found personal directory contacts
     *   limit :     {number}    // Limit specified at the search request
     *   offset :    {number}    // The position of first retrieved contact entry
     *   total :     {number}    // Total number of available personal directory contacts (May be different from the number of returned contacts)
     * }
     */
    public async getPersonalContactsByCriteria(options: any = null): Promise<{ contacts: DirectoryContact[]; limit: number; offset: number; total: number; }> {
        try {
            // Get and Prepare request url query parameters
            const urlOptions: any = {};

            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalContactsByCriteria -- User does not have the profile feature PERSONAL_DIRECTORY");

            if (options) {
                if (options.format && (typeof options.format === "string") && options.format.length) {
                    urlOptions.format = options.format;
                }
                else {
                    // Default options when the one specified by the client is wrong
                    urlOptions.format = "full";
                }
                if (options.limit) { urlOptions.limit = options.limit; }
                if (options.offset) { urlOptions.offset = options.offset; }

                if (options.searchPattern) {
                    switch (options.searchBy) {
                        case "phoneNumbers": // Allows to filter the list of directory entries on the number provided in this option.
                            urlOptions.phoneNumbers = encodeURIComponent(options.searchPattern);
                            break;
                        // case "tags":
                        //     urlOptions.searchBy = encodeURIComponent(options.searchPattern);
                        //     break;
                        case "name": // Allows to filter the list of directory entries of user type on the name
                        default:
                            urlOptions.name = encodeURIComponent(options.searchPattern);
                            break;
                    }
                }
            } else {
                // Default options when no specified by client
                urlOptions.format = "full";
            }

            // Prepare request url
            const directoryScope: string = "personal";
            const url = new URL(`${this.directoryPortalURL}/entries?view=${directoryScope}`);
            const urlParams = new URLSearchParams(urlOptions).toString();
            url.search = urlParams;

            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info(`[PersonalDirectoryService] getPersonalContactsByCriteria success -- Found ${responseData.total} contact(s)`);
            const contactsData = responseData.data;
            const persDirContacts: DirectoryContact[] = [];

            contactsData.forEach((contactData) => {
                // Convert the personal directory contact (server format) into a directory contact object
                const persDirContact: DirectoryContact = DirectoryContact.createFromData(contactData);

                // Create a new Contact object or update the existing one with the retrieved personal contact
                this.createOrUpdateContact(persDirContact);

                persDirContacts.push(persDirContact);
            });

            return { contacts: persDirContacts, limit: responseData.limit, offset: responseData.offset, total: responseData.total };

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalContactsByCriteria');
            this.logger.error(`[PersonalDirectoryService] getPersonalContactsByCriteria failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method getPersonalContactCardById
     * @description This API allows users to retrieve from server one contact card in the users's personal directory from its identifier
     * API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectory Rainbow Company Directory portal - directory - Get a directory entry data})
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} id Identifier of contact to retrieve
     * @param {string} [format] Allows to retrieve more or less contact details in response.
     *      - small: id, directoryType, firstName, lastName
     *      - medium: id, directoryType, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @returns {Promise<DirectoryContact>} the {@link DirectoryContact} retrieved
     */
    public async getPersonalContactCardById(id: string, format: string = 'full'): Promise<DirectoryContact | null> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalContactCardById -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/${id}?format=${format}`;
            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());

            this.logger.info(`[PersonalDirectoryService] getPersonalContactCardById for ${id} success from server`);

            // Convert the personal directory contact (server format) into a directory contact object
            const persDirContact: DirectoryContact = DirectoryContact.createFromData(responseData.data);

            // Create a new Contact object or update the existing one with the retrieved personal contact
            this.createOrUpdateContact(persDirContact);

            return persDirContact;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalContactCardById');
            this.logger.error(`[PersonalDirectoryService] getPersonalContactCardById failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method getPersonalContactById
     * @description This API allows users to retrieve from server a contact in the users's personal directory from its identifier
     * API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectory Rainbow Company Directory portal - directory - Get a directory entry data})
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} id Identifier of contact to retrieve
     * @param {string} [format] Allows to retrieve more or less contact details in response.
     *      - small: id, directoryType, firstName, lastName
     *      - medium: id, directoryType, firstName, lastName, workPhoneNumbers
     *      - full: all fields.
     * @returns {Promise<Contact>} the retrieved contact
     */
    public async getPersonalContactById(id: string, format: string = 'full'): Promise<Contact | null> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalContactById -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/${id}?format=${format}`;
            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());

            this.logger.info(`[PersonalDirectoryService] getPersonalContactById for ${id} success from server`);

            // Convert the personal directory contact (server format) into a directory contact object
            const persDirContact: DirectoryContact = DirectoryContact.createFromData(responseData.data);

            // Create a new Contact object or update the existing one with the retrieved personal contact
            const contact: Contact = this.createOrUpdateContact(persDirContact);

            return contact;
        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalContactById');
            this.logger.error(`[PersonalDirectoryService] failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method getNumberPersonalContact
     * @description This API allows users to retrieve from Contact internal database the personal contacts created by the user
     * @memberof PersonalDirectoryService
     * @public
     * @returns {Contact[]} the retrieved personal contacts
     */
    public getMyContacts(): Contact[] {
        const allContacts = this.contactService.getContacts();

        const myPersonalContacts = allContacts.filter((contact) => {
            return (contact.personalDirectoryFlag);
        });

        return myPersonalContacts;
    }

    /**
     * This API allows users to search personal directory contacts by specifying a pattern
     * (API request to {@link https://api.openrainbow.org/directory/#api-directory-GetDirectoryList Rainbow Company Directory portal - directory - Get a list of directory entries data})
     * @public
     * @description
     * Search contacts in the personal directory of the user by using a specified pattern to filter items.
     * This search is only available if the user has the feature PERSONAL_DIRECTORY in its profile.
     * Concerning 'extendedMode' in 'options' parameter:
     * If requested and the user has the feature SEARCH_BY_TAGS in its profile, a multi-criteria search will then be performed.
     * The multi-criteria search applies on fields firstName, lastName, jobTitle, companyName, department and tags.
     * If this option is not specified or the user does not have the feature SEARCH_BY_TAGS in his profile,
     * the search is based on names only (first name, last name and company name).
     * @param {string} searchPattern Pattern to search
     * @param {DirectorySearchOptions} [options] Search options {@link DirectorySearchOptions}
     * @returns {Promise<DirectoryInternalSearchResults[]>} Resolves with an array of {@link DirectoryInternalSearchResults} results and
     * the total number of directory items matching the criteria.
     * @memberof PersonalDirectoryService
     */
    public async searchPersonalContacts(searchPattern: string, options?: DirectorySearchOptions): Promise<DirectoryInternalSearchResults> {
        try {
            // First, Check user licence
            if (!this.personalDirectoryEnabled) {
                this.logger.debug(`[PersonalDirectoryService] searchPersonalContacts -- User does not have the profile feature PERSONAL_DIRECTORY`);
                return { contacts: [], total: 0 };
            }

            this.logger.info(`[PersonalDirectoryService] search personal contacts with pattern ${anonymizer(searchPattern)}`);

            // Define useful search options
            const searchOptions: any = {};
            searchOptions.type = "all";
            searchOptions.view = "personal";
            searchOptions.format = "full";

            if (options?.limit) { searchOptions.limit = options.limit; }

            // Simple OR Multi criteria search ?
            const searchByTagAllowed: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SEARCH_BY_TAGS);
            let searchType: string = "";

            if (options?.extendedMode) {
                if (searchByTagAllowed) {
                    searchType = options.extendedMode ? "search" : "name";
                } else {
                    if (options.extendedMode) {
                        this.logger.warn(`[PersonalDirectoryService] searchPersonalContacts -- 'extendedMode' option not allowed and ignored `);
                    }
                    searchType = "name";
                }
            } else {
                searchType = "name";
            }
            if (searchType === "name") {
                searchOptions.name = searchPattern;
            } else {
                searchOptions.search = searchPattern;
            }

            // Build Request for Server
            const url: URL = new URL(`${this.directoryPortalURL}/entries`);
            const urlParams: string = new URLSearchParams(searchOptions).toString();
            const headers: any = this.authService.getRequestHeader();
            url.search = urlParams;
            const response: any = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const contacts: any = responseData.data;

            this.logger.info(`[PersonalDirectoryService] search success -- Found ${responseData.total} contact(s)`);

            const foundContacts: Contact[] = contacts.map((contactData) => {
                // Convert the personal directory contact (server format) into a directory contact object
                const persDirContact: DirectoryContact = DirectoryContact.createFromData(contactData);

                // Create a new Contact object or update the existing one with the retrieved personal contact
                return this.createOrUpdateContact(persDirContact);
            });
            this.logger.info(`[PersonalDirectoryService] searchPersonalContacts -- Return ${foundContacts.length} contact(s)`);

            return { contacts: foundContacts, total: responseData.total };
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, 'searchPersonalContacts');
            this.logger.error(`[PersonalDirectoryService] ${errorInfo.message}`);
            if (errorInfo.error.status === 403) {
                // Request rejected by server probably because of a user license problem
                // Return a "no results found" like reponse
                return { contacts: [], total: 0 };
            }
            throw errorInfo.error;
        }
    }

    /**
     * Method getPersonalDirectoryContactFromNumber
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} phoneNumber The number
     * @returns {Contact} Found contact or null otherwise
     */
    public getPersonalDirectoryContactFromNumber(phoneNumber: string): Contact | null {
        // First, Check licence
        if (!this.personalDirectoryEnabled) {
            // this.logger.info(`[PersonalDirectoryService] getPersonalDirectoryContactFromNumber -- User does not have the profile feature PERSONAL_DIRECTORY`);
            return null;
        }

        const foundContactByNumber = this.contactService.getDirectoryContactByPhoneNumber(phoneNumber);
        if (foundContactByNumber) {
            // Checks if the contact is from the personal directory
            // If not the case, ignore the found contact
            if (!foundContactByNumber.personalDirectoryFlag)
                return null;

            return foundContactByNumber;
        }

        return null;
    }

    // Management of personal contacts Tags
    // --------------------------------------------

    /**
     * Method getPersonalDirectoryTags
     * @description This API allows users to retrieve from server all the tags assigned to the personal directory entries
     * (API request to {@link https://api.openrainbow.org/directory/#api-personal_directory_tags-GetPersonalDirectoryTags Rainbow Company Directory portal - personal directory tags - List all tags assigned to personal directory entries
     * @memberof PersonalDirectoryService
     * @public
     * @returns {Promise<string[]>} tags Array of tags
     */
    public async getPersonalDirectoryTags(): Promise<string[]> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalDirectoryTags -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/personal/tags`;
            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const tagsList = responseData.data.tags || [];
            this.logger.info(`[PersonalDirectoryService] Get personal directory tags success from server -- Found ${tagsList.length} occurence(s)`);
            return tagsList;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalDirectoryTags');
            this.logger.error(`[PersonalDirectoryService] getPersonalDirectoryTags failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method deletePersonalDirectoryTag
     * @description This API allows users to delete from server the specified tag assigned to the personal directory entries
     * (API request to {@link https://api.openrainbow.org/directory/#api-personal_directory_tags-DelPersonalDirectoryTags Rainbow Company Directory portal - personal directory tags - Remove a given tag from all the personal directory entries
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} tag Name of tag to remove
     * @returns {Promise<void>} Resolves when deletion operation is done
     */
    public async deletePersonalDirectoryTag(tag: string): Promise<void> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] deletePersonalDirectoryTag -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/personal/tags?tag=${encodeURIComponent(tag)}`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'DELETE', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            this.logger.info(`[PersonalDirectoryService] Deletion of tag "${tag}" success -- Found ${responseData.data.found} occurence(s)`);

            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_TAG_DELETE, tag));

            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'deletePersonalDirectoryTag');
            this.logger.error(`[PersonalDirectoryService] deletePersonalDirectoryTag failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method renamePersonalDirectoryTag
     * @description This API allows users to rename a tag for all assigned personal directory entries
     * (API request to {@link https://api.openrainbow.org/directory/#api-personal_directory_tags-PutPersonalDirectoryTags personal directory tags - Rename a tag for all assigned directory entries
     * @memberof PersonalDirectoryService
     * @public
     * @param {string} tag Name of the tag to be renamed
     * @param {string} newTagName New name of the tag
     * @returns {Promise<void>} Resolves when rename operation is done
     */
    public async renamePersonalDirectoryTag(tag: string, newTagName: string): Promise<void> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] renamePersonalDirectoryTag -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/personal/tags?tag=${encodeURIComponent(tag)}`;
            const headers: any = this.authService.getPostHeader();
            const response: Response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({ newTagName: newTagName }) });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());

            this.logger.info(`[PersonalDirectoryService] Renaming of tag from "${tag}" to "${newTagName}" success -- Found ${responseData.data.found} occurence(s)`);

            this.rxSubject.next(RBEvent.create(PersonalDirectoryEvent.PERSONAL_DIRECTORY_TAG_RENAME, { oldTagName: tag, newTagName: newTagName }));

            return;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'renamePersonalDirectoryTag');
            this.logger.error(`[PersonalDirectoryService] renamePersonalDirectoryTag failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    /**
     * Method getPersonalDirectoryTagsStats
     * @description This API can be used to list all the tags being assigned to the personal directory entries of the logged in user, with the number of directory entries for each tags.
     * (API request to {@linkhttps://api.openrainbow.org/directory/#api-personal_directory_tags-GetPersonalDirectoryTagsStats personal directory tags - Return stats regarding tags of personal directory entries
     * @memberof PersonalDirectoryService
     * @public
     * @returns {Promise<{ count: number; tag: string }[]>} Array of stats with the number of use (count) and the tag name
     */
    public async getPersonalDirectoryTagsStats(): Promise<{ count: number; tag: string; }[]> {
        try {
            // First, Check licence
            if (!this.personalDirectoryEnabled)
                throw Error("[PersonalDirectoryService] getPersonalDirectoryTagsStats -- User does not have the profile feature PERSONAL_DIRECTORY");

            const url: string = `${this.directoryPortalURL}/entries/personal/tags/stats`;
            const headers: any = this.authService.getRequestHeader();
            const response: Response = await fetch(url, { method: 'GET', headers });
            if (!response.ok) { throw (response); }
            const responseData: any = (await response.json());
            const tagsStats = responseData.data.stats || [];

            this.logger.info(`[PersonalDirectoryService] getPersonalDirectoryTagsStats success -- Found ${tagsStats.length} tags(s)`);
            return tagsStats;

        } catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getPersonalDirectoryTagsStats');
            this.logger.error(`[PersonalDirectoryService] getPersonalDirectoryTagsStats failure: ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // ====================================================
    //                 PRIVATE FUNCTIONS
    // ====================================================

    /**
     * Method createContactFromPersonalDirectoryData
     * @description Create a new contact based on Contact model from a personal directory object (DirectoryContact)
     * @memberof PersonalDirectoryService
     * @private
     * @param {DirectoryContact} persDirContact The created personal directory contact
     * @returns {Contact} Created contact or null if creation failed
     */
    private createContactFromPersonalDirectoryData(persDirContact: DirectoryContact): Contact | null {
        const contact: Contact = this.contactService.createDirectoryContact(persDirContact.id, true);

        if (!contact) {
            this.logger.error(`[PersonalDirectoryService] createContactFromPersonalDirectoryData -- Contact creation failed for ${persDirContact.id}`);
            return null;
        }

        // Set specific data which does not come from the contact itself but useful to manage and identify
        // the personal contact in contact Model
        // These flags must be set BEFORE the update of the created contact names (via 'updateName') because some are deduced
        // from the firstname/lastname OR the company name and the contact type.
        contact.companyDirectoryFlag = false;
        contact.personalDirectoryFlag = true;
        contact.userId = persDirContact.userId;

        // Fields coming from the personal contact card
        contact.email = persDirContact.eMail;
        contact.jobTitle = persDirContact.jobTitle;
        contact.department = persDirContact.department;
        contact.companyName = persDirContact.companyName;

        contact.updateName(persDirContact.firstName, persDirContact.lastName);
        contact.setAvatarFromDefault();

        // Address
        contact.street = persDirContact.street;
        contact.city = persDirContact.city;
        contact.postalCode = persDirContact.postalCode;
        // contact.country = this.countryService.getCountryName(persDirContact.country);
        // contact.state = this.countryService.getStateName(persDirContact.country, persDirContact.state);
        contact.country = persDirContact.country;
        contact.state = persDirContact.state;

        // Phone numbers
        contact.businessPhones = persDirContact.workPhoneNumbers;
        contact.mobilePhones = persDirContact.mobilePhoneNumbers;
        contact.otherPhoneNumbers = persDirContact.otherPhoneNumbers;

        contact.note1 = persDirContact.custom1;
        contact.note2 = persDirContact.custom2;
        contact.tags = persDirContact.tags ? persDirContact.tags : [];

        return contact;
    }

    /**
     * Method updateContactFromPersonalDirectoryData
     * @description Update an existing contact with a personal directory object (DirectoryContact)
     * @memberof PersonalDirectoryService
     * @private
     * @param {DirectoryContact} persDirContact The updated personal directory object
     * @returns {Contact} Updated contact or null if update failed
     */
    private updateContactFromPersonalDirectoryData(persDirContact: DirectoryContact): Contact | null {
        const contact: Contact = this.contactService.getContactFromCacheByJid(persDirContact.id);

        if (!contact) {
            this.logger.error(`[PersonalDirectoryService] updateContactFromData -- Unable to find contact for ${persDirContact.id} for update`);
            return null;
        }

        // Set contact type (this data should not have been modified but for security only)
        contact.companyDirectoryFlag = false;
        contact.personalDirectoryFlag = true;
        contact.userId = persDirContact.userId;

        // Fields coming from the personal contact card
        contact.email = persDirContact.eMail;
        contact.jobTitle = persDirContact.jobTitle;
        contact.department = persDirContact.department;
        contact.companyName = persDirContact.companyName;

        contact.updateName(persDirContact.firstName, persDirContact.lastName);
        contact.setAvatarFromDefault();

        // Address
        contact.street = persDirContact.street;
        contact.city = persDirContact.city;
        contact.postalCode = persDirContact.postalCode;
        // contact.country = this.countryService.getCountryName(persDirContact.country);
        // contact.state = this.countryService.getStateName(persDirContact.country, persDirContact.state);
        contact.country = persDirContact.country;
        contact.state = persDirContact.state;

        // Phone numbers
        contact.businessPhones = persDirContact.workPhoneNumbers;
        contact.mobilePhones = persDirContact.mobilePhoneNumbers;
        contact.otherPhoneNumbers = persDirContact.otherPhoneNumbers;

        contact.note1 = persDirContact.custom1;
        contact.note2 = persDirContact.custom2;
        contact.tags = persDirContact.tags ? persDirContact.tags : [];

        return contact;
    }

    /**
     * Method createOrUpdateContact
     * @description Create or update an existing contact with a personal directory object (DirectoryContact)
     * @memberof PersonalDirectoryService
     * @private
     * @param {DirectoryContact} persDirContact A personal directory object just retrieved from server
     * @returns {Contact} Created or Updated contact
     */
    private createOrUpdateContact(persDirContact: DirectoryContact): Contact | null {
        // Create  a new Contact object or update the existing one with the retrieved personal contact
        const existingContact: Contact = this.contactService.getContactFromCacheByJid(persDirContact.id);

        if (existingContact) {
            const updatedContact: Contact = this.updateContactFromPersonalDirectoryData(persDirContact);
            return updatedContact;
        }
        // Else Contact creation
        const createdContact: Contact = this.createContactFromPersonalDirectoryData(persDirContact);
        return createdContact;
    }

    /**
     * Method createPersonalDirectoryDataFromContact
     *
     * Create a personal directory object (DirectoryContact) from a contact based on Contact model
     * Useful to update on server side a personal directory contact from data stored in a contact
     * @memberof PersonalDirectoryService
     * @private
     * @param {Contact} contactData The contact associated to the given personal directory
     * @returns {DirectoryContact} Personal directory contact object used for update on server side
     */
    private createPersonalDirectoryDataFromContact(contactData: Contact): DirectoryContact {
        const persDirectoryContact = new DirectoryContact("personal");

        // Fields coming from the personal contact card
        persDirectoryContact.firstName = contactData.firstname;
        persDirectoryContact.lastName = contactData.lastname;
        persDirectoryContact.eMail = contactData.email;
        persDirectoryContact.jobTitle = contactData.jobTitle;
        persDirectoryContact.department = contactData.department;
        persDirectoryContact.companyName = contactData.companyName;

        // Address
        persDirectoryContact.street = contactData.street;
        persDirectoryContact.city = contactData.city;
        persDirectoryContact.postalCode = contactData.postalCode;

        // persDirectoryContact.country = this.countryService.getCountryCodeFromName(contactData.country);
        persDirectoryContact.country = contactData.country;
        persDirectoryContact.state = this.countryService.getStateCodeFromName(contactData.country, contactData.state);

        // Phone numbers
        persDirectoryContact.workPhoneNumbers = contactData.businessPhones;
        persDirectoryContact.mobilePhoneNumbers = contactData.mobilePhones;
        persDirectoryContact.otherPhoneNumbers = contactData.otherPhoneNumbers;

        persDirectoryContact.custom1 = contactData.note1;
        persDirectoryContact.custom2 = contactData.note2;
        persDirectoryContact.tags = contactData.tags;

        return persDirectoryContact;
    }

    /********************************************************/
    /** DOWNLOAD CSV TEMPLATE                              **/
    /********************************************************/

    public async downloadCsvTemplateFile(): Promise<any> {
        this.logger.info(`[PersonalDirectoryService] >downloadCsvTemplateFile`);

        try {
            // Build Request for Server
            const url: URL = new URL(this.massProPortalURL + "directories/template?personal=true");

            const headers: any = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }

            const csvContent: any = await response.blob();

            this.logger.info(`[PersonalDirectoryService] GET Template success`);

            return csvContent;
        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "downloadCsvTemplateFile");
            this.logger.error(`[PersonalDirectoryService] GET Template failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** CHECK CSV FILE CONTENT                             **/
    /********************************************************/

    public async checkDirectoryImportCsvContent(csvContent): Promise<any> {
        this.logger.info(`[PersonalDirectoryService] >checkDirectoryImportCsvContent`);

        try {
            // Build Request for Server
            const url: URL = new URL(this.massProPortalURL + "directories/imports/check");
            const headers: any = this.authService.getPostHeader("text/csv; charset=utf-8");
            const response: any = await fetch(url.toString(), { method: 'POST', headers: headers, body: csvContent });

            if (!response.ok) { throw (response); }

            const checkResult: any = await response.json();

            this.logger.info(`[PersonalDirectoryService] Check CSV Content Success`);

            return checkResult.data;

        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "checkDirectoryImportCsvContent");
            this.logger.error(`[PersonalDirectoryService] Check CSV Content failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** GET CSV FILE IMPORTS HISTORY                       **/
    /********************************************************/

    public async getDirectoryImportReport(): Promise<any> {
        this.logger.info(`[PersonalDirectoryService] >getDirectoryImportReport`);

        try {
            // Build Request for Server
            const url: URL = new URL(this.massProPortalURL + "directories/imports/personal/details");
            const headers: any = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }

            const result: any = await response.json();

            this.logger.info(`[PersonalDirectoryService] GET Personal Imports Success`);

            return result.data;

        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "getDirectoryImportReport");
            this.logger.error(`[PersonalDirectoryService] GET Personal Imports failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** IMPORT CSV FILE PERSONAL DIRECTORY                 **/
    /********************************************************/

    public async importPersonalDirectory(csvContent: any): Promise<any> {
        this.logger.info(`[PersonalDirectoryService] >importPersonalDirectory`);

        try {
            // Build Request for Server
            const url: URL = new URL(this.massProPortalURL + "directories/imports");
            const headers: any = this.authService.getPostHeader("text/csv; charset=utf-8");
            const response: any = await fetch(url.toString(), { method: 'POST', headers: headers, body: csvContent });

            if (!response.ok) { throw (response); }

            const checkResult: any = await response.json();

            this.logger.info(`[PersonalDirectoryService] Import Personal Directory Success`);

            return checkResult.data;

        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "importPersonalDirectory");
            this.logger.error(`[PersonalDirectoryService] Import Personal Directory failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

    /********************************************************/
    /** EXPORT CSV FILE PERSONAL DIRECTORY                 **/
    /********************************************************/

    public async exportPersonalDirectory(): Promise<any> {
        this.logger.info(`[PersonalDirectoryService] >exportPersonalDirectory`);

        try {
            // Build Request for Server
            const url: URL = new URL(this.massProPortalURL + "directories/synchronize");
            const headers: any = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers: headers });

            if (!response.ok) { throw (response); }

            const dataResult: any = await response.json();

            let csvContent = '';
            const keys = [];
            if (dataResult.data.length > 0) {
                let keyIndex = 0;
                dataResult.data.forEach((entry) => {
                    for (const [key, value] of Object.entries(entry)) {
                        if (keys.indexOf(key) === -1) {
                            keys.push(key);
                            if (keyIndex > 0) csvContent += ";";
                            csvContent += key;
                            keyIndex++;
                        }
                    }
                });

                dataResult.data.forEach((csvLine) => {
                    csvContent += "\n";
                    keys.forEach((key, index) => {
                        if (index > 0) csvContent += ";";
                        if (csvLine[key] !== undefined) csvContent += csvLine[key];
                    });
                });
            }
            const exportDataBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8,' });

            this.logger.info(`[PersonalDirectoryService] GET Personal Directory Success`);

            return exportDataBlob;

        } catch (error) {
            const errorInfo: ErrorHelperInfo = await this.errorHelperService.getErrorInfo(error, "exportPersonalDirectory");
            this.logger.error(`[PersonalDirectoryService] Export Personal Directory failure -- ${errorInfo.error.message}`);
            if (error.data && error.data.errorCode === 401) {
                this.rxSubject.next(RBEvent.create('ON_SESSION_EXPIRED_NOTIFICATION_EVENT'));
            }
            throw errorInfo.error;
        }
    }

}
